import {
    Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, IconButton, Checkbox,
    Stack
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import React, { useState } from "react";
import ModalSelecionarCotacao from "./ModalSelecionarCotacao";
import { formatarMonetárioParaNúmero, formatarValorMonetario } from "../../../../../../../../helpers/masks";
import { CotacaoRealizada } from "../..";
import { ConfirmProvider } from "material-ui-confirm";
import { ProdutoGerarOc } from "..";

export default function Step01({
    produtos,
    cotacoes,
    onSelectCotacao,
    selectedItens,
    onSelectItem
}: Step01Props) {
    const [modalOpen, setModalOpen] = useState(false);
    const [produtoEdit, setProdutoEdit] = useState<ProdutoGerarOc | null>(null);

    function handleEdit(produto: ProdutoGerarOc) {
        setProdutoEdit(produto);
        setModalOpen(true);
    }

    function handleCloseModal() {
        setModalOpen(false);
        setProdutoEdit(null);
    }

    function handleSelectCotacaoModal(cotacaoId: number) {
        if (produtoEdit) {
            onSelectCotacao(produtoEdit.produto_id, cotacaoId);
            if (!selectedItens.includes(produtoEdit.produto_id)) {
                onSelectItem(produtoEdit.produto_id, true);
            }
        }
        handleCloseModal();
    }

    return (
        <Box>
            <Typography variant="h5" fontWeight={600} mb={1} sx={{ fontSize: { xs: '1rem', md: '1.12rem' } }}>
                Geração de Ordem de compra
            </Typography>
            <Typography fontSize={13} color="text.secondary" mb={2} sx={{ fontSize: { xs: '0.87rem', md: '0.92rem' } }}>
                Selecione os produtos e vincule a cotação desejada para cada item.
            </Typography>
            <Table sx={{ mb: { xs: 1.5, md: 2 }, fontSize: { xs: '0.85rem', md: '0.92rem' } }}>
                <TableHead>
                    <TableRow>
                        <TableCell />
                        <TableCell><b>Produto</b></TableCell>
                        <TableCell align="right"><b>Qtd. solicitada</b></TableCell>
                        <TableCell align="right"><b>Valor unitário</b></TableCell>
                        <TableCell align="right"><b>Valor total</b></TableCell>
                        <TableCell align="right"><b>Prev. entrega</b></TableCell>
                        <TableCell><b>Cotação</b></TableCell>
                        <TableCell />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {produtos.map((produto) => {
                        const cotacaoSelecionada = cotacoes
                            .find(cotacao => cotacao.id === produto.cotacao_id);
                        const disabledActions = produto.status_cotacao === "OC Emitida";

                        return (
                            <TableRow key={produto.id} hover sx={{ fontSize: { xs: '0.85rem', md: '0.92rem' } }}>
                                <TableCell>
                                    <Checkbox
                                        checked={selectedItens.includes(produto.produto_id)}
                                        onChange={e => onSelectItem(produto.produto_id, e.target.checked)}
                                        disabled={disabledActions}
                                    />
                                </TableCell>
                                <TableCell >
                                    <Typography variant="subtitle2">{produto.produto_id} {produto.nome}</Typography>
                                    {produto.observacoes && (
                                        <Typography
                                            variant="subtitle2"
                                            color="text.secondary" sx={{ fontSize: { xs: '0.82rem', md: '0.9rem' } }}>
                                            {produto.observacoes}
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell align="right">
                                    {produto.quantidade} {produto.unidade_medida}
                                </TableCell>
                                <TableCell align="right">
                                    {formatarValorMonetario(produto.valor_unitario)}
                                </TableCell>
                                <TableCell align="right" sx={{ color: getValorTotalColor(produto.valor_desconto?.toString() || '0', produto.valor_acrescimo?.toString() || '0') }}>
                                    {formatarValorMonetario((produto.valor_unitario - formatarMonetárioParaNúmero(produto.valor_desconto || '0') + formatarMonetárioParaNúmero(produto.valor_acrescimo || '0')) * produto.quantidade)}
                                </TableCell>
                                <TableCell align="right">
                                    {produto.prazo_entrega || "-"}
                                </TableCell>
                                <TableCell>
                                    {cotacaoSelecionada ?
                                        <Stack>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Cód: {cotacaoSelecionada.id}
                                            </Typography>
                                            <Typography variant="subtitle2">
                                                {cotacaoSelecionada.fornecedor}
                                            </Typography>
                                        </Stack>
                                        :
                                        "-"
                                    }
                                </TableCell>
                                <TableCell align="center" sx={{ py: 0.5 }}>
                                    <IconButton
                                        size="small"
                                        onClick={() => handleEdit(produto)}
                                        disabled={disabledActions}
                                    >
                                        <EditIcon fontSize="inherit" />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
            {produtoEdit && (
                <ConfirmProvider defaultOptions={{
                    title: 'Confirmação',
                    cancellationText: "Cancelar",
                    confirmationText: "Continuar",
                    confirmationButtonProps: { variant: 'contained', color: 'primary' }
                }}>
                    <ModalSelecionarCotacao
                        open={modalOpen}
                        produto={produtoEdit}
                        cotacoes={cotacoes}
                        selectedCotacaoId={produtoEdit.cotacao_id}
                        onSelect={handleSelectCotacaoModal}
                        onClose={handleCloseModal}
                    />
                </ConfirmProvider>
            )}
        </Box>
    );
}

interface Step01Props {
    produtos: ProdutoGerarOc[];
    cotacoes: CotacaoRealizada[];
    onSelectCotacao: (produto_id: number, cotacao_id: number, desconto?: string, acrescimo?: string) => void;
    selectedItens: number[];
    onSelectItem: (produto_id: number, selected: boolean) => void;
}

function getValorTotalColor(descontos: string, acrescimos: string) {
    const desconto = formatarMonetárioParaNúmero(descontos);
    const acrescimo = formatarMonetárioParaNúmero(acrescimos);

    if (acrescimo > 0 && acrescimo > desconto) {
        return 'red';
    }
    if (desconto > 0 && desconto > acrescimo) {
        return 'green';
    }
    return 'inherit';
}